import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Globe, Instagram, Facebook, ArrowRight, Search } from "lucide-react";
import "./eventpage.css";

const CATEGORY_OPTIONS = [
  "Food & Beverage", "Fashion & Apparel", "Jewelry & Accessories",
  "Home & Decor", "Beauty & Wellness", "Art & Crafts",
  "Kids & Toys", "Books & Stationery", "Tech & Gadgets", "Services",
];

const PAGE_SIZE = 12;

const EventVendors = ({ 
  eventId: propEventId, 
  eventName: propEventName, 
  eventKey: propEventKey 
}) => {
  const routeParams = useParams();

  // Pick up route parameter if props weren't explicitly passed
  // Handles common param names: :eventId, :id, :eventKey, :slug
  const routeEventIdentifier = 
    routeParams.eventId || 
    routeParams.id || 
    routeParams.eventKey || 
    routeParams.slug;

  const [resolvedEventId, setResolvedEventId] = useState(propEventId || null);
  const [eventResolving, setEventResolving] = useState(!propEventId);
  const [eventLookupError, setEventLookupError] = useState("");

  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeCategory, setActiveCategory] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  // 1. Resolve event ID dynamically
  useEffect(() => {
    // If eventId was passed directly as a prop, use it immediately
    if (propEventId) {
      setResolvedEventId(propEventId);
      setEventResolving(false);
      return;
    }

    const resolveEvent = async () => {
      setEventResolving(true);
      setEventLookupError("");

      const identifier = propEventKey || propEventName || routeEventIdentifier;

      if (!identifier) {
        setEventLookupError("No event specified.");
        setEventResolving(false);
        return;
      }

      // Check if the identifier is a UUID
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

      let query = supabase
        .from("events")
        .select("id, name, status, start_date")
        .neq("status", "draft");

      if (isUuid) {
        query = query.eq("id", identifier);
      } else if (propEventKey) {
        query = query.eq("event_key", propEventKey);
      } else {
        query = query.ilike("name", `%${identifier}%`);
      }

      const { data, error: evErr } = await query
        .order("start_date", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (evErr || !data) {
        setEventLookupError("Vendors aren't available yet — check back closer to the event.");
        setEventResolving(false);
        return;
      }

      setResolvedEventId(data.id);
      setEventResolving(false);
    };

    resolveEvent();
  }, [propEventId, propEventName, propEventKey, routeEventIdentifier]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // 2. Load confirmed vendors for the resolved event
  const loadVendors = useCallback(async (id) => {
    if (!id) return;
    setVendorsLoading(true);
    setError("");

    // Only fetch approved vendors
    const { data: applications, error: appErr } = await supabase
      .from("vendor_applications")
      .select("vendor_id, categories")
      .eq("event_id", id)
      .eq("status", "approved");

    if (appErr || !applications || applications.length === 0) {
      setVendors([]);
      setVendorsLoading(false);
      return;
    }

    const categoriesByVendor = {};
    applications.forEach((app) => {
      const cats = (app.categories || [])
        .flat(Infinity)
        .filter((c) => typeof c === "string" && c.trim().length > 0);
      categoriesByVendor[app.vendor_id] = [...new Set(cats)];
    });

    const vendorIds = Object.keys(categoriesByVendor);

    if (vendorIds.length === 0) {
      setVendors([]);
      setVendorsLoading(false);
      return;
    }

    const { data: profiles, error: profileErr } = await supabase
      .from("public_vendor_profiles")
      .select("*")
      .in("id", vendorIds);

    if (profileErr) {
      setError("Couldn't load vendors right now — please try again shortly.");
      setVendors([]);
    } else {
      const merged = (profiles || []).map((p) => ({
        ...p,
        categories: categoriesByVendor[p.id] || [],
      }));
      setVendors(merged);
    }
    setVendorsLoading(false);
  }, []);

  useEffect(() => {
    if (resolvedEventId) loadVendors(resolvedEventId);
  }, [resolvedEventId, loadVendors]);

  // Client-side filter
  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => {
      if (activeCategory && !v.categories?.includes(activeCategory)) return false;
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const haystack = `${v.brand_name || ""} ${(v.categories || []).join(" ")}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [vendors, activeCategory, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [activeCategory, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / PAGE_SIZE));
  const paginatedVendors = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredVendors.slice(start, start + PAGE_SIZE);
  }, [filteredVendors, page]);

  const availableCategories = useMemo(() => {
    const present = new Set();
    vendors.forEach((v) => (v.categories || []).forEach((c) => c && present.add(c)));
    return CATEGORY_OPTIONS.filter((c) => present.has(c));
  }, [vendors]);

  if (eventResolving) {
    return (
      <div className="ev-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="ev-card ev-card--skeleton" />
        ))}
      </div>
    );
  }

  if (eventLookupError) {
    return <p className="ev-empty-note">{eventLookupError}</p>;
  }

  return (
    <div className="ev-wrap">
      <div className="ev-controls">
        <div className="ev-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search vendors…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {availableCategories.length > 0 && (
        <div className="ev-pill-row">
          <button
            type="button"
            className={`ev-pill ${activeCategory === null ? "ev-pill--active" : ""}`}
            onClick={() => setActiveCategory(null)}
          >
            All
          </button>
          {availableCategories.map((cat) => (
            <button
              type="button"
              key={cat}
              className={`ev-pill ${activeCategory === cat ? "ev-pill--active" : ""}`}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {error && <p className="ev-error">{error}</p>}

      {vendorsLoading ? (
        <div className="ev-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ev-card ev-card--skeleton" />
          ))}
        </div>
      ) : filteredVendors.length === 0 ? (
        <div className="ev-empty">
          <p>
            {vendors.length === 0
              ? "Vendors will appear here once confirmed for this event."
              : "No vendors match your search filters."}
          </p>
        </div>
      ) : (
        <>
          <div className="ev-grid">
            {paginatedVendors.map((vendor) => (
              <VendorCard key={vendor.id} vendor={vendor} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="ev-pagination">
              <button
                type="button"
                className="ev-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>
              <span className="ev-page-status">Page {page} of {totalPages}</span>
              <button
                type="button"
                className="ev-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// URL Helpers
const EMPTY_VALUES = ["na", "n/a", "none", "-", ""];
const isEmptyLink = (raw) => !raw || EMPTY_VALUES.includes(raw.trim().toLowerCase());

const toWebsiteUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  return /^https?:\/\//i.test(val) ? val : `https://${val.replace(/^\/+/, "")}`;
};

const toInstagramUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  return /^https?:\/\//i.test(val) ? val : `https://instagram.com/${val.replace(/^@/, "")}`;
};

const toFacebookUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  return /^https?:\/\//i.test(val) ? val : `https://facebook.com/${val.replace(/^\/+/, "")}`;
};

const VendorCard = ({ vendor }) => {
  const websiteUrl = toWebsiteUrl(vendor.website);
  const instagramUrl = toInstagramUrl(vendor.instagram);
  const facebookUrl = toFacebookUrl(vendor.facebook);

  return (
    <div className="ev-card">
      <div className="ev-card-logo">
        {vendor.logo_url ? (
          <img src={vendor.logo_url} alt={`${vendor.brand_name} logo`} loading="lazy" />
        ) : (
          <span className="ev-card-logo-placeholder">
            {vendor.brand_name?.[0]?.toUpperCase() || "?"}
          </span>
        )}
      </div>

      <h3 className="ev-card-name">{vendor.brand_name}</h3>

      {vendor.categories?.length > 0 && (
        <div className="ev-card-chip-row">
          {vendor.categories.slice(0, 2).map((cat) => (
            <span key={cat} className="ev-card-chip">{cat}</span>
          ))}
        </div>
      )}

      <div className="ev-card-socials">
        {websiteUrl && (
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer" aria-label="Website">
            <Globe size={16} />
          </a>
        )}
        {instagramUrl && (
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <Instagram size={16} />
          </a>
        )}
        {facebookUrl && (
          <a href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <Facebook size={16} />
          </a>
        )}
      </div>

      <Link to={`/v/${vendor.slug}`} className="ev-card-view-btn">
        View profile <ArrowRight size={14} />
      </Link>
    </div>
  );
};

export default EventVendors;